import React, { useState, useMemo } from 'react';
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Info,
  DollarSign,
  Calendar,
  Percent
} from 'lucide-react';
import { ColumnMapping } from '../../types/templateTypes';

interface ColumnMapperProps {
  sourceColumns: string[];
  currentMappings: Record<string, ColumnMapping>;
  onMappingsChange: (mappings: Record<string, ColumnMapping>) => void;
}

// Standard database fields we expect to map
const STANDARD_FIELDS: Array<{
  field: string;
  label: string;
  required: boolean;
  category: string;
  transform?: 'date' | 'currency' | 'percentage';
}> = [
  { field: 'broker', label: 'Broker Name', required: true, category: 'broker' },
  { field: 'brokerEmail', label: 'Broker Email', required: false, category: 'broker' },
  { field: 'brokerPhone', label: 'Broker Phone', required: false, category: 'broker' },
  { field: 'office', label: 'Office Name', required: false, category: 'office' },
  { field: 'customerName', label: 'Customer Name', required: true, category: 'customer' },
  { field: 'loanId', label: 'Loan ID/Reference', required: true, category: 'loan' },
  { field: 'loanAmount', label: 'Loan Amount', required: true, category: 'loan', transform: 'currency' },
  { field: 'originalLoanAmount', label: 'Original Loan Amount', required: false, category: 'loan', transform: 'currency' },
  { field: 'loanBalance', label: 'Loan Balance', required: false, category: 'loan', transform: 'currency' },
  { field: 'lender', label: 'Lender', required: true, category: 'loan' },
  { field: 'productType', label: 'Product Type', required: false, category: 'loan' },
  { field: 'settlementDate', label: 'Settlement Date', required: true, category: 'dates', transform: 'date' },
  { field: 'invoicedDate', label: 'Invoiced Date', required: false, category: 'dates', transform: 'date' },
  { field: 'dischargeDate', label: 'Discharge Date', required: false, category: 'dates', transform: 'date' },
  { field: 'amountExGst', label: 'Amount (Ex GST)', required: true, category: 'amounts', transform: 'currency' },
  { field: 'gst', label: 'GST', required: false, category: 'amounts', transform: 'currency' },
  { field: 'amountIncGst', label: 'Amount (Inc GST)', required: false, category: 'amounts', transform: 'currency' },
  { field: 'commissionRate', label: 'Commission Rate', required: false, category: 'amounts', transform: 'percentage' },
  { field: 'paymentType', label: 'Payment Type', required: true, category: 'classification' },
  { field: 'referrer', label: 'Referrer', required: false, category: 'referral' },
  { field: 'salesperson', label: 'Salesperson', required: false, category: 'referral' },
  { field: 'comments', label: 'Comments', required: false, category: 'metadata' },
  { field: 'arrearsAmount', label: 'Arrears Amount', required: false, category: 'amounts', transform: 'currency' }
];

const ColumnMapper: React.FC<ColumnMapperProps> = ({
  sourceColumns,
  currentMappings,
  onMappingsChange
}) => {
  const [autoMappingInProgress, setAutoMappingInProgress] = useState(false);

  // Auto-map columns based on fuzzy matching
  const autoMapColumns = () => {
    setAutoMappingInProgress(true);

    const newMappings: Record<string, ColumnMapping> = { ...currentMappings };

    STANDARD_FIELDS.forEach(field => {
      // Skip if already mapped
      if (Object.values(newMappings).some(m => m.sourceColumn === field.field)) {
        return;
      }

      // Try to find matching column
      const lowerFieldName = field.label.toLowerCase().replace(/[^a-z0-9]/g, '');

      for (const col of sourceColumns) {
        const lowerCol = col.toLowerCase().replace(/[^a-z0-9]/g, '');

        // Check for exact or close matches
        if (
          lowerCol === lowerFieldName ||
          lowerCol.includes(lowerFieldName) ||
          lowerFieldName.includes(lowerCol) ||
          (field.field === 'loanId' && (lowerCol.includes('reference') || lowerCol.includes('loanid'))) ||
          (field.field === 'broker' && lowerCol.includes('introducer')) ||
          (field.field === 'customerName' && lowerCol.includes('customer')) ||
          (field.field === 'amountExGst' && (lowerCol === 'income' || lowerCol.includes('amtexgst'))) ||
          (field.field === 'loanAmount' && lowerCol.includes('loanamount')) ||
          (field.field === 'originalLoanAmount' && lowerCol.includes('originalloanamount'))
        ) {
          newMappings[field.field] = {
            sourceColumn: col,
            transform: field.transform,
            dateFormat: field.transform === 'date' ? 'DD/MM/YYYY' : undefined
          };
          break;
        }
      }
    });

    onMappingsChange(newMappings);
    setTimeout(() => setAutoMappingInProgress(false), 500);
  };

  // Handle manual mapping change
  const handleMappingChange = (dbField: string, sourceColumn: string | undefined) => {
    const newMappings = { ...currentMappings };

    if (!sourceColumn || sourceColumn === '') {
      delete newMappings[dbField];
    } else {
      const fieldDef = STANDARD_FIELDS.find(f => f.field === dbField);
      newMappings[dbField] = {
        sourceColumn,
        transform: fieldDef?.transform,
        dateFormat: fieldDef?.transform === 'date' ? 'DD/MM/YYYY' : undefined
      };
    }

    onMappingsChange(newMappings);
  };

  // Calculate mapping status
  const mappingStatus = useMemo(() => {
    const requiredFields = STANDARD_FIELDS.filter(f => f.required);
    const mappedRequired = requiredFields.filter(f => currentMappings[f.field]);
    const mappedOptional = STANDARD_FIELDS.filter(f => !f.required && currentMappings[f.field]);

    return {
      requiredMapped: mappedRequired.length,
      requiredTotal: requiredFields.length,
      optionalMapped: mappedOptional.length,
      optionalTotal: STANDARD_FIELDS.filter(f => !f.required).length,
      isComplete: mappedRequired.length === requiredFields.length
    };
  }, [currentMappings]);

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      broker: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      customer: 'bg-green-500/20 text-green-400 border-green-500/30',
      loan: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      dates: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      amounts: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      classification: 'bg-red-500/20 text-red-400 border-red-500/30',
      referral: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      metadata: 'bg-slate-500/20 text-slate-400 border-slate-500/30'
    };
    return colors[category] || 'bg-slate-500/20 text-slate-400 border-slate-500/30';
  };

  const getTransformIcon = (transform?: string) => {
    switch (transform) {
      case 'currency':
        return <DollarSign className="w-3 h-3" />;
      case 'date':
        return <Calendar className="w-3 h-3" />;
      case 'percentage':
        return <Percent className="w-3 h-3" />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white font-medium text-lg">Column Mapping</h3>
          <p className="text-slate-400 text-sm mt-1">
            Map source columns from the file to database fields
          </p>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              mappingStatus.isComplete ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
            }`}>
              Required: {mappingStatus.requiredMapped}/{mappingStatus.requiredTotal}
            </span>
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">
              Optional: {mappingStatus.optionalMapped}/{mappingStatus.optionalTotal}
            </span>
          </div>

          <button
            onClick={autoMapColumns}
            disabled={autoMappingInProgress}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 text-white rounded-lg transition-colors text-sm font-medium"
          >
            <RefreshCw className={`w-4 h-4 ${autoMappingInProgress ? 'animate-spin' : ''}`} />
            Auto-Map
          </button>
        </div>
      </div>

      {!mappingStatus.isComplete && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-red-400 font-medium">Missing Required Mappings</h4>
              <p className="text-slate-400 text-sm mt-1">
                {mappingStatus.requiredTotal - mappingStatus.requiredMapped} required field{mappingStatus.requiredTotal - mappingStatus.requiredMapped > 1 ? 's' : ''} must be mapped before the template can be saved.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-slate-900/50 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-800/50">
              <tr>
                <th className="px-4 py-3 text-left text-slate-400 font-medium text-sm">Database Field</th>
                <th className="px-4 py-3 text-left text-slate-400 font-medium text-sm">Category</th>
                <th className="px-4 py-3 text-left text-slate-400 font-medium text-sm">Source Column</th>
                <th className="px-4 py-3 text-left text-slate-400 font-medium text-sm">Transform</th>
                <th className="px-4 py-3 text-center text-slate-400 font-medium text-sm">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {STANDARD_FIELDS.map(field => {
                const isMapped = !!currentMappings[field.field];
                return (
                  <tr key={field.field} className="hover:bg-slate-800/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-white text-sm">{field.label}</span>
                        {field.required && (
                          <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">
                            Required
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded border text-xs font-medium ${getCategoryColor(field.category)}`}>
                        {field.category.charAt(0).toUpperCase() + field.category.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={currentMappings[field.field]?.sourceColumn || ''}
                        onChange={e => handleMappingChange(field.field, e.target.value)}
                        className="w-full px-3 py-1.5 bg-slate-800/50 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-blue-500"
                      >
                        <option value="">Select source column</option>
                        {sourceColumns.map(col => (
                          <option key={col} value={col}>{col}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      {field.transform && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-700/50 text-slate-300 text-xs rounded">
                          {getTransformIcon(field.transform)}
                          {field.transform}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isMapped ? (
                        <CheckCircle className="w-5 h-5 text-green-400 inline" />
                      ) : field.required ? (
                        <XCircle className="w-5 h-5 text-red-400 inline" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-yellow-400 inline" />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-blue-400 font-medium">Column Mapping Tips</h4>
            <ul className="text-slate-400 text-sm mt-2 space-y-1">
              <li>• Use Auto-Map to automatically detect common column names</li>
              <li>• Required fields must be mapped for the template to work</li>
              <li>• Date fields will be automatically parsed based on format</li>
              <li>• Currency fields will be converted to integer cents for storage</li>
              <li>• The system will handle variations in column naming (e.g., "Introducer Name" → "Broker")</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColumnMapper;